/**
 * Gmail-Calendar-Tasks Triangle Mycelium Connector
 *
 * Bridges Gmail, Calendar, and Task services using mycelium event backbone.
 * Implements intelligent parsing of emails to automatically create calendar events and tasks.
 *
 * Key Features:
 * - Email parsing for scheduling language detection
 * - Automatic calendar event creation from emails
 * - Task generation from calendar events
 * - Context-aware action suggestions
 * - Deduplication to prevent duplicate events/tasks
 *
 * Flow Examples:
 * 1. Email: "Meeting with client tomorrow at 2pm"
 *    → Creates calendar event
 *    → Creates task "Prepare for client meeting"
 *
 * 2. Calendar event created manually
 *    → Suggests task creation for meeting prep
 *
 * 3. Task created with due date
 *    → Suggests calendar event creation
 *
 * @module mycelium-gmail-calendar-tasks
 * @since 2026-02-20
 */

import { myceliumEventBackbone, type MyceliumEvent } from "./mycelium-event-backbone.service";
import { getMyceliumTransport } from "./mycelium-transport-registry";
import { logger } from "../lib/logger";
import { typedSupabase } from "../lib/typed-supabase";

// ============================================================================
// SCHEDULING LANGUAGE PATTERNS
// ============================================================================

/**
 * Common date/time patterns in emails
 * Ordered by specificity (most specific first)
 */
const SCHEDULING_PATTERNS = [
  // Specific date + time patterns
  {
    pattern: /(?:on\s+)?(?:next\s+)?(?:this\s+)?(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+at\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/gi,
    type: 'specific_datetime' as const,
  },
  {
    pattern: /(?:on\s+)?(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\s+at\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/gi,
    type: 'specific_datetime' as const,
  },
  {
    pattern: /(tomorrow|today)\s+at\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/gi,
    type: 'specific_datetime' as const,
  },

  // Date-only patterns
  {
    pattern: /(?:on\s+)?(?:next\s+)?(?:this\s+)?(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/gi,
    type: 'date_only' as const,
  },
  {
    pattern: /(tomorrow|today|yesterday)/gi,
    type: 'date_only' as const,
  },

  // Generic scheduling keywords
  {
    pattern: /\b(meeting|call|appointment|session|conference|interview|demo|presentation)\b/gi,
    type: 'scheduling_keyword' as const,
  },
];

/**
 * Action keywords that suggest task creation
 */
const ACTION_KEYWORDS = [
  'prepare', 'review', 'send', 'complete', 'follow up', 'followup',
  'schedule', 'book', 'arrange', 'organize', 'plan', 'draft',
  'create', 'update', 'finalize', 'submit', 'approve'
];

/**
 * Meeting-related keywords
 */
const MEETING_KEYWORDS = [
  'meeting', 'call', 'appointment', 'session', 'conference',
  'interview', 'demo', 'presentation', 'discussion', 'sync',
  'standup', 'retrospective', 'planning', 'review'
];

// ============================================================================
// CONFIGURATION
// ============================================================================

const CONFIG = {
  // Minimum confidence score to auto-create events (0-1)
  minConfidenceAutoCreate: 0.8,

  // Minimum confidence to suggest (0-1)
  minConfidenceSuggest: 0.5,

  // Default event duration in minutes
  defaultEventDuration: 60,

  // Default task lead time before event (in minutes)
  defaultTaskLeadTime: 60,

  // Deduplication window in milliseconds (24 hours)
  deduplicationWindow: 24 * 60 * 60 * 1000,
} as const;

// ============================================================================
// TYPES
// ============================================================================

interface SchedulingInfo {
  hasSchedulingLanguage: boolean;
  confidence: number;
  suggestedTitle: string;
  suggestedDescription: string;
  suggestedDate?: Date;
  suggestedTime?: { hour: number; minute: number };
  meetingType?: string;
  actionItems?: string[];
}

interface ParsedEmail {
  messageId: string;
  subject: string;
  body: string;
  from: string;
  to: string[];
  receivedAt: Date;
  threadId?: string;
}

// ============================================================================
// CONNECTOR SERVICE
// ============================================================================

export class GmailCalendarTasksConnector {
  private initialized = false;
  private recentCreations = new Map<string, number>(); // For deduplication

  /**
   * Initialize connector and subscribe to events
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      logger.debug("GmailCalendarTasksConnector already initialized");
      return;
    }

    logger.info("🔺 Gmail-Calendar-Tasks Triangle connector initializing");

    // Subscribe to email events
    myceliumEventBackbone.subscribe({
      name: "GmailCalendarTasksConnector-Email",
      domains: ["integration"],
      eventTypes: ["gmail_message_received", "gmail_message_sent"],
      handler: this.handleEmailEvent.bind(this),
      priority: 7,
    });

    // Subscribe to calendar events
    myceliumEventBackbone.subscribe({
      name: "GmailCalendarTasksConnector-Calendar",
      domains: ["calendar"],
      eventTypes: ["event_created", "event_updated"],
      handler: this.handleCalendarEvent.bind(this),
      priority: 7,
    });

    // Subscribe to task events
    myceliumEventBackbone.subscribe({
      name: "GmailCalendarTasksConnector-Task",
      domains: ["task"],
      eventTypes: ["task_created", "task_due_date_set"],
      handler: this.handleTaskEvent.bind(this),
      priority: 7,
    });

    this.initialized = true;
    logger.info("✅ Gmail-Calendar-Tasks Triangle connector ready");
    logger.info("💡 Emails with scheduling language will auto-create calendar events and tasks");
  }

  /**
   * Handle incoming/outgoing email events
   */
  private async handleEmailEvent(event: MyceliumEvent): Promise<void> {
    const { type, userId, payload } = event;

    if (!userId) {
      logger.debug("Skipping email event without userId", { eventId: event.id, type });
      return;
    }

    try {
      // Parse email content
      const parsedEmail = this.parseEmailFromPayload(payload);
      if (!parsedEmail) {
        logger.debug("Could not parse email from payload", { eventId: event.id });
        return;
      }

      // Analyze for scheduling language
      const schedulingInfo = this.analyzeSchedulingLanguage(parsedEmail);

      logger.debug("📧 Email analyzed for scheduling content", {
        messageId: parsedEmail.messageId,
        hasScheduling: schedulingInfo.hasSchedulingLanguage,
        confidence: schedulingInfo.confidence,
      });

      // Auto-create or suggest based on confidence
      if (schedulingInfo.hasSchedulingLanguage) {
        if (schedulingInfo.confidence >= CONFIG.minConfidenceAutoCreate) {
          await this.autoCreateFromEmail(userId, parsedEmail, schedulingInfo, event);
        } else if (schedulingInfo.confidence >= CONFIG.minConfidenceSuggest) {
          await this.suggestCreationFromEmail(userId, parsedEmail, schedulingInfo, event);
        }
      }
    } catch (error) {
      logger.error("Error handling email event", {
        eventId: event.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  /**
   * Handle calendar event creation/update
   */
  private async handleCalendarEvent(event: MyceliumEvent): Promise<void> {
    const { type, userId, payload } = event;

    if (!userId || type !== "event_created") {
      return;
    }

    try {
      const eventTitle = payload.title as string || payload.summary as string;
      const eventDate = payload.startTime as string || payload.start as string;
      const eventId = payload.eventId as string || payload.id as string;

      if (!eventTitle || !eventDate || !eventId) {
        logger.debug("Calendar event missing required fields", { payload });
        return;
      }

      // Check if this should generate a prep task
      const shouldCreateTask = this.shouldCreatePrepTask(eventTitle, payload);

      if (shouldCreateTask) {
        logger.debug("📅 Calendar event suggests task creation", {
          eventId,
          eventTitle,
        });

        await this.createPrepTaskForEvent(userId, {
          eventId,
          eventTitle,
          eventDate,
          eventDescription: payload.description as string,
        }, event);
      }
    } catch (error) {
      logger.error("Error handling calendar event", {
        eventId: event.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  /**
   * Handle task creation with due dates
   */
  private async handleTaskEvent(event: MyceliumEvent): Promise<void> {
    const { type, userId, payload } = event;

    if (!userId) {
      return;
    }

    try {
      const taskTitle = payload.title as string;
      const dueDate = payload.dueDate as string || payload.due_date as string;
      const taskId = payload.taskId as string || payload.id as string;

      if (!taskTitle || !dueDate || !taskId) {
        return;
      }

      // Check if task has scheduling language that warrants calendar event
      const hasSchedulingIntent = MEETING_KEYWORDS.some(keyword =>
        taskTitle.toLowerCase().includes(keyword)
      );

      if (hasSchedulingIntent && !this.isDuplicate(`task-${taskId}`, 'calendar')) {
        logger.debug("✅ Task suggests calendar event creation", {
          taskId,
          taskTitle,
        });

        await this.suggestCalendarEventFromTask(userId, {
          taskId,
          taskTitle,
          dueDate,
          taskDescription: payload.description as string,
        }, event);
      }
    } catch (error) {
      logger.error("Error handling task event", {
        eventId: event.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  // ============================================================================
  // EMAIL PARSING
  // ============================================================================

  /**
   * Parse email from event payload
   */
  private parseEmailFromPayload(payload: Record<string, unknown>): ParsedEmail | null {
    try {
      const messageId = payload.messageId as string || payload.id as string;
      const subject = payload.subject as string || '';
      const body = payload.body as string || payload.snippet as string || '';
      const from = payload.from as string || '';
      const to = (payload.to as string[]) || [];
      const receivedAt = payload.receivedAt as string || payload.internalDate as string;

      if (!messageId || !subject) {
        return null;
      }

      return {
        messageId,
        subject,
        body,
        from,
        to,
        receivedAt: receivedAt ? new Date(receivedAt) : new Date(),
        threadId: payload.threadId as string,
      };
    } catch (error) {
      logger.error("Error parsing email payload", { error });
      return null;
    }
  }

  /**
   * Analyze email for scheduling language
   */
  private analyzeSchedulingLanguage(email: ParsedEmail): SchedulingInfo {
    const combinedText = `${email.subject} ${email.body}`.toLowerCase();
    let confidence = 0;
    let suggestedDate: Date | undefined;
    let suggestedTime: { hour: number; minute: number } | undefined;
    let meetingType: string | undefined;
    const actionItems: string[] = [];

    // Check for scheduling patterns
    let hasDatetime = false;
    let hasDate = false;
    let hasKeyword = false;

    for (const { pattern, type } of SCHEDULING_PATTERNS) {
      const matches = combinedText.match(pattern);
      if (matches && matches.length > 0) {
        if (type === 'specific_datetime') {
          hasDatetime = true;
          confidence += 0.4;
          // Parse date/time from matches
          const parsed = this.parseDateTimeFromMatch(matches[0]);
          if (parsed.date) suggestedDate = parsed.date;
          if (parsed.time) suggestedTime = parsed.time;
        } else if (type === 'date_only') {
          hasDate = true;
          confidence += 0.2;
          const parsed = this.parseDateFromMatch(matches[0]);
          if (parsed) suggestedDate = parsed;
        } else if (type === 'scheduling_keyword') {
          hasKeyword = true;
          confidence += 0.1;
          meetingType = matches[0];
        }
      }
    }

    // Check for meeting keywords
    for (const keyword of MEETING_KEYWORDS) {
      if (combinedText.includes(keyword)) {
        confidence += 0.1;
        if (!meetingType) meetingType = keyword;
        break;
      }
    }

    // Check for action items
    for (const keyword of ACTION_KEYWORDS) {
      if (combinedText.includes(keyword)) {
        actionItems.push(keyword);
        confidence += 0.05;
      }
    }

    // Cap confidence at 1.0
    confidence = Math.min(confidence, 1.0);

    const hasSchedulingLanguage = hasDatetime || (hasDate && hasKeyword);

    return {
      hasSchedulingLanguage,
      confidence,
      suggestedTitle: email.subject || 'Meeting',
      suggestedDescription: this.extractDescription(email.body),
      suggestedDate,
      suggestedTime,
      meetingType,
      actionItems,
    };
  }

  /**
   * Parse date and time from match
   */
  private parseDateTimeFromMatch(match: string): { date?: Date; time?: { hour: number; minute: number } } {
    // Simple parsing - in production, use a library like chrono-node
    const result: { date?: Date; time?: { hour: number; minute: number } } = {};

    // Parse "tomorrow at 2pm"
    if (match.includes('tomorrow')) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      result.date = tomorrow;
    } else if (match.includes('today')) {
      result.date = new Date();
    }

    // Parse time
    const timeMatch = match.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
    if (timeMatch) {
      let hour = parseInt(timeMatch[1]);
      const minute = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
      const meridiem = timeMatch[3]?.toLowerCase();

      if (meridiem === 'pm' && hour < 12) hour += 12;
      if (meridiem === 'am' && hour === 12) hour = 0;

      result.time = { hour, minute };
    }

    return result;
  }

  /**
   * Parse date from match
   */
  private parseDateFromMatch(match: string): Date | undefined {
    if (match.includes('tomorrow')) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow;
    } else if (match.includes('today')) {
      return new Date();
    }

    // Parse day of week
    const dayMap: Record<string, number> = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
      thursday: 4, friday: 5, saturday: 6
    };

    for (const [day, dayNum] of Object.entries(dayMap)) {
      if (match.includes(day)) {
        const now = new Date();
        const currentDay = now.getDay();
        const daysUntil = (dayNum - currentDay + 7) % 7 || 7;
        const targetDate = new Date();
        targetDate.setDate(now.getDate() + daysUntil);
        return targetDate;
      }
    }

    return undefined;
  }

  /**
   * Extract clean description from email body
   */
  private extractDescription(body: string): string {
    // Take first 300 characters, clean up
    return body
      .substring(0, 300)
      .replace(/\s+/g, ' ')
      .trim();
  }

  // ============================================================================
  // ACTION EXECUTION
  // ============================================================================

  /**
   * Auto-create calendar event and task from email
   */
  private async autoCreateFromEmail(
    userId: string,
    email: ParsedEmail,
    schedulingInfo: SchedulingInfo,
    sourceEvent: MyceliumEvent
  ): Promise<void> {
    // Check for duplicates
    if (this.isDuplicate(email.messageId, 'event')) {
      logger.debug("Skipping duplicate event creation", { messageId: email.messageId });
      return;
    }

    logger.info("🎯 Auto-creating calendar event and task from email", {
      messageId: email.messageId,
      confidence: schedulingInfo.confidence,
      suggestedTitle: schedulingInfo.suggestedTitle,
    });

    // Mark as created for deduplication
    this.markCreated(email.messageId, 'event');

    // Publish event for downstream services to handle
    // (Actual calendar/task creation would be handled by dedicated services)
    await getMyceliumTransport().publish({
      domain: "integration",
      type: "gmail_scheduling_detected",
      userId,
      payload: {
        source: "email",
        action: "auto_create",
        messageId: email.messageId,
        schedulingInfo,
        email: {
          subject: email.subject,
          from: email.from,
        },
        sourceEventId: sourceEvent.id,
      },
    });
  }

  /**
   * Suggest calendar event and task creation from email
   */
  private async suggestCreationFromEmail(
    userId: string,
    email: ParsedEmail,
    schedulingInfo: SchedulingInfo,
    sourceEvent: MyceliumEvent
  ): Promise<void> {
    logger.info("💡 Suggesting calendar event and task from email", {
      messageId: email.messageId,
      confidence: schedulingInfo.confidence,
    });

    await getMyceliumTransport().publish({
      domain: "integration",
      type: "gmail_scheduling_detected",
      userId,
      payload: {
        source: "email",
        action: "suggest",
        messageId: email.messageId,
        schedulingInfo,
        email: {
          subject: email.subject,
          from: email.from,
        },
        sourceEventId: sourceEvent.id,
      },
    });
  }

  /**
   * Create prep task for calendar event
   */
  private async createPrepTaskForEvent(
    userId: string,
    eventInfo: {
      eventId: string;
      eventTitle: string;
      eventDate: string;
      eventDescription?: string;
    },
    sourceEvent: MyceliumEvent
  ): Promise<void> {
    if (this.isDuplicate(eventInfo.eventId, 'task')) {
      return;
    }

    this.markCreated(eventInfo.eventId, 'task');

    await getMyceliumTransport().publish({
      domain: "calendar",
      type: "prep_task_suggested",
      userId,
      payload: {
        eventId: eventInfo.eventId,
        eventTitle: eventInfo.eventTitle,
        eventDate: eventInfo.eventDate,
        suggestedTaskTitle: `Prepare for: ${eventInfo.eventTitle}`,
        suggestedDueDate: this.calculatePrepTaskDueDate(eventInfo.eventDate),
        sourceEventId: sourceEvent.id,
      },
    });
  }

  /**
   * Suggest calendar event from task
   */
  private async suggestCalendarEventFromTask(
    userId: string,
    taskInfo: {
      taskId: string;
      taskTitle: string;
      dueDate: string;
      taskDescription?: string;
    },
    sourceEvent: MyceliumEvent
  ): Promise<void> {
    await getMyceliumTransport().publish({
      domain: "task",
      type: "calendar_event_suggested",
      userId,
      payload: {
        taskId: taskInfo.taskId,
        taskTitle: taskInfo.taskTitle,
        suggestedEventTitle: taskInfo.taskTitle,
        suggestedEventDate: taskInfo.dueDate,
        sourceEventId: sourceEvent.id,
      },
    });
  }

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Check if calendar event should generate prep task
   */
  private shouldCreatePrepTask(title: string, payload: Record<string, unknown>): boolean {
    const titleLower = title.toLowerCase();

    // Don't create prep tasks for all-day events
    if (payload.allDay === true) {
      return false;
    }

    // Check for meeting keywords
    return MEETING_KEYWORDS.some(keyword => titleLower.includes(keyword));
  }

  /**
   * Calculate when prep task should be due (before event)
   */
  private calculatePrepTaskDueDate(eventDate: string): string {
    const date = new Date(eventDate);
    date.setMinutes(date.getMinutes() - CONFIG.defaultTaskLeadTime);
    return date.toISOString();
  }

  /**
   * Check for duplicate creation
   */
  private isDuplicate(id: string, type: 'event' | 'task' | 'calendar'): boolean {
    const key = `${id}-${type}`;
    const lastCreated = this.recentCreations.get(key);

    if (lastCreated) {
      const timeSince = Date.now() - lastCreated;
      return timeSince < CONFIG.deduplicationWindow;
    }

    return false;
  }

  /**
   * Mark as created for deduplication
   */
  private markCreated(id: string, type: 'event' | 'task' | 'calendar'): void {
    const key = `${id}-${type}`;
    this.recentCreations.set(key, Date.now());

    // Clean up old entries (older than window)
    const cutoff = Date.now() - CONFIG.deduplicationWindow;
    for (const [key, timestamp] of this.recentCreations.entries()) {
      if (timestamp < cutoff) {
        this.recentCreations.delete(key);
      }
    }
  }

  /**
   * Cleanup on disposal
   */
  async dispose(): Promise<void> {
    this.recentCreations.clear();
    this.initialized = false;
    logger.info("🧹 Gmail-Calendar-Tasks Triangle connector disposed");
  }

}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const gmailCalendarTasksConnector = new GmailCalendarTasksConnector();
